/* hys-cart-savings.js
 * Compact subtotal row with a True Classic-style "Show details" expander
 * (snippets/cart-details.liquid, compact_totals branch — drawer only).
 *
 * Collapsed:  Subtotal · Show details        ~~$164.98~~ $149.99
 * Expanded:   one row per discount title (line-level Crew Pack / Clearance
 *             + cart-level codes) and a total-savings row.
 *
 * Reactive via the Cart store (same pattern as hys-cart-tier-nudge.js):
 * amounts, strike-through original, and the panel all update live as the
 * cart changes. The toggle is ALWAYS visible so the footer reads the same
 * with and without a discount (Peter 2026-07-19); only the struck original
 * is discount-gated. The vendor <cart-total> keeps updating the amount.
 */
import Cart from 'cart-store';
import { formatMoney } from 'utils';

class HysCartSavings extends HTMLElement {
  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-toggle]');
    this.panel = this.querySelector('[data-panel]');
    this.originalEl = this.querySelector('[data-original]');
    if (!this.toggleBtn || !this.panel || !this.originalEl) return;
    this.open = false;
    this.onToggle = () => {
      this.open = !this.open;
      this.#renderOpenState();
    };
    this.toggleBtn.addEventListener('click', this.onToggle);
    this.unsubscribe = Cart.subscribe((state) => this.#update(state.cart));
    this.#update(Cart.getState().cart);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.toggleBtn && this.onToggle) this.toggleBtn.removeEventListener('click', this.onToggle);
  }

  #money(cents) {
    try {
      return formatMoney(cents, window.theme.money_format).replace(/[.,]00$/, '');
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #update(cart) {
    if (!cart) return;
    /* Live "(N)" in the sibling View cart button (cart-details.liquid,
     * show_cart_count). Piggybacks on this element's subscription — it
     * already does an initial update on connect, which the vendor
     * cart-quantity-info pattern lacks for template-cloned markup. */
    const countEl = (this.closest('.cart__controls') || document).querySelector('[data-hys-view-count]');
    if (countEl) countEl.textContent = cart.item_count > 0 ? ` (${cart.item_count})` : '';
    const saved = (cart.original_total_price || 0) - (cart.total_price || 0);
    /* One generic "Discounts" row (Peter 2026-07-19): naming each discount
     * here duplicated the per-line badges, and a lone title row + Total
     * savings row said the same number twice. */
    const hasSavings = saved > 0;

    this.originalEl.toggleAttribute('hidden', saved <= 0);
    /* innerHTML, not textContent: theme money_format wraps amounts in
     * <span class=money> — textContent printed the markup literally. */
    if (saved > 0) this.originalEl.innerHTML = this.#money(cart.original_total_price);

    /* Toggle stays visible either way so the 1-item and discounted carts
     * share the same footer layout (Peter 2026-07-19). */
    this.toggleBtn.removeAttribute('hidden');

    this.panel.innerHTML = hasSavings ? `
      <div class="hys-cart-savings__row hys-cart-savings__row--total">
        <span>Discounts</span><span>-${this.#money(saved)}</span>
      </div>` : `
      <div class="hys-cart-savings__row">
        <span>No discounts applied</span>
      </div>`;

    this.#renderOpenState();
  }

  #renderOpenState() {
    this.panel.toggleAttribute('hidden', !this.open);
    this.toggleBtn.textContent = this.open ? 'Hide details' : 'Show details';
    this.toggleBtn.setAttribute('aria-expanded', this.open ? 'true' : 'false');
  }
}

if (!customElements.get('hys-cart-savings')) {
  customElements.define('hys-cart-savings', HysCartSavings);
}
